import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import API, { downloadAuthed, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Signature, DownloadSimple, Prohibit, PencilSimple, CheckCircle, Clock, Eye, FileText } from "@phosphor-icons/react";
import { ROLE_LABELS } from "@/lib/esignApi";

const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  in_progress: { label: "Awaiting signatures", cls: "bg-amber-50 text-amber-700" },
  awaiting_solicitor: { label: "Ready to finalise", cls: "bg-blue-50 text-blue-700" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700" },
  voided: { label: "Voided", cls: "bg-rose-50 text-rose-700" },
};

// Embedded signing-request manager for the Documents tab. Creation happens
// per-document (via the "Send for signing" action on each PDF), so this panel
// only lists + manages existing requests (edit draft, finalise, void, download).
export default function ESignPanel({ caseId, refreshKey, onEditDraft, onChanged, autoFinaliseEnvelopeId }) {
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finalise, setFinalise] = useState(null);
  const [autoOpened, setAutoOpened] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/signing/envelopes`, { params: { case_id: caseId } });
      setEnvelopes(data.envelopes || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Deep-linked from the "all parties signed" email: auto-open the finalise
  // (date-the-deed / PG82) dialog for the specified envelope, once.
  useEffect(() => {
    if (autoOpened || !autoFinaliseEnvelopeId || envelopes.length === 0) return;
    const env = envelopes.find((e) => e.id === autoFinaliseEnvelopeId);
    if (env && env.status === "awaiting_solicitor") {
      setFinalise(env);
      setAutoOpened(true);
    } else if (env && env.status === "completed") {
      toast.info("This signing request has already been finalised — the signed PDF and PG82 are filed in Documents.");
      setAutoOpened(true);
    }
  }, [autoFinaliseEnvelopeId, envelopes, autoOpened]);

  const previewSigned = async (env) => {
    try {
      const { data } = await API.get(`/signing/envelopes/${env.id}/preview.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const active = envelopes.filter((e) => e.status !== "voided");

  return (
    <div className="mb-3 pb-3 border-b border-slate-100" data-testid="esign-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
          <Signature weight="bold" className="w-3 h-3" /> Signing requests
        </span>
        <span className="text-[11px] text-slate-400">— use “Send for signing” on any PDF below</span>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : active.length === 0 ? (
        <p className="text-xs text-slate-400">No signing requests yet.</p>
      ) : (
        <div className="space-y-2">
          {active.map((env) => {
            const st = STATUS[env.status] || STATUS.draft;
            return (
              <div key={env.id} className="border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid={`esign-row-${env.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 text-sm truncate">{env.title}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{env.mode === "deed" ? "Deed + witness" : "Standard"}</span>
                    </div>
                    {env.parties?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {env.parties.map((p) => (
                          <span key={p.party_id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600">
                            {p.status === "complete" ? <CheckCircle weight="fill" className="w-3 h-3 text-emerald-600" /> : <Clock className="w-3 h-3 text-slate-400" />}
                            {ROLE_LABELS[p.role] || p.role}: {p.name || "—"}
                            {p.status === "witness_pending" && <span className="text-cyan-600">· witness</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {env.status === "draft" && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEditDraft(env)} data-testid={`esign-edit-${env.id}`}><PencilSimple className="w-3.5 h-3.5 mr-1" /> Edit & send</Button>
                    )}
                    {env.status === "awaiting_solicitor" && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => previewSigned(env)} data-testid={`esign-preview-${env.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview signed</Button>
                        <Button size="sm" className="h-7 text-xs" onClick={() => setFinalise(env)} data-testid={`esign-finalise-${env.id}`}>Date & sign PG82</Button>
                      </>
                    )}
                    {env.status === "completed" && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadAuthed(`/documents/${env.signed_document_id}/download`, `Signed - ${env.title}.pdf`)} data-testid={`esign-download-${env.id}`}><DownloadSimple className="w-3.5 h-3.5 mr-1" /> Signed</Button>
                        {env.pg82_document_id && (<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadAuthed(`/documents/${env.pg82_document_id}/download`, `PG82 - ${env.title}.pdf`)} data-testid={`esign-pg82-${env.id}`}>PG82</Button>)}
                        {env.audit_document_id && (<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadAuthed(`/documents/${env.audit_document_id}/download`, `Certificate - ${env.title}.pdf`)} data-testid={`esign-cert-${env.id}`}>Certificate</Button>)}
                      </>
                    )}
                    {["draft", "in_progress", "awaiting_solicitor"].includes(env.status) && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => voidEnv(env, () => { load(); onChanged?.(); })} data-testid={`esign-void-${env.id}`}><Prohibit className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {finalise && <FinaliseDialog envelope={finalise} onClose={() => setFinalise(null)} onDone={() => { setFinalise(null); load(); onChanged?.(); }} />}
    </div>
  );
}

async function voidEnv(env, reload) {
  if (!window.confirm(`Void "${env.title}"? Signers will no longer be able to sign.`)) return;
  try { await API.post(`/signing/envelopes/${env.id}/void`); toast.success("Voided"); reload(); }
  catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
}

function FinaliseDialog({ envelope, onClose, onDone }) {
  const [deedDate, setDeedDate] = useState(new Date().toISOString().slice(0, 10));
  const [deedType, setDeedType] = useState("TR1");
  const [busy, setBusy] = useState(false);
  const docs = envelope.documents || [];
  const willSplit = envelope.split_output && docs.length > 1;
  const submit = async () => {
    setBusy(true);
    try {
      await API.post(`/signing/envelopes/${envelope.id}/finalise`, { deed_date: deedDate, deed_type: deedType });
      toast.success("Deed dated — signed PDF, PG82 and certificate filed to Documents");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="esign-finalise-dialog">
        <DialogHeader>
          <DialogTitle>Date the deed & sign the PG82</DialogTitle>
          <DialogDescription>All parties and witnesses have signed. Set the date of the deed; we'll stamp it, file the signed PDF, and generate the PG82 conveyancer's certificate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Date of deed</Label><Input type="date" value={deedDate} onChange={(e) => setDeedDate(e.target.value)} data-testid="finalise-deed-date" /></div>
          <div><Label className="text-xs">Deed type</Label>
            <Select value={deedType} onValueChange={setDeedType}>
              <SelectTrigger data-testid="finalise-deed-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["TR1", "TP1", "DS1", "Legal Charge", "Lease", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="finalise-output-preview">
            <p className="text-xs font-semibold text-slate-700 mb-1.5">Files that will be filed to Documents</p>
            <ul className="space-y-1 text-[11px] text-slate-600">
              <li className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Signed — {envelope.title}.pdf {docs.length > 1 && <span className="text-slate-400">(merged bundle, {docs.length} docs)</span>}</li>
              {willSplit && docs.map((d) => (
                <li key={d.source_document_id || d.filename} className="flex items-center gap-1.5 pl-4" data-testid={`finalise-split-${d.source_document_id || d.filename}`}>
                  <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Signed — {(d.filename || "document").replace(/\.pdf$/i, "")}.pdf <span className="text-slate-400">(separate copy)</span>
                </li>
              ))}
              {envelope.mode === "deed" && <li className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> PG82 Conveyancer's Certificate.pdf</li>}
              <li className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Certificate of Completion.pdf</li>
            </ul>
            {docs.length > 1 && !envelope.split_output && (
              <p className="text-[11px] text-slate-400 mt-1.5">Tip: enable “file each document separately” when creating the request to also get individual signed copies.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={async () => {
            try { const { data } = await API.get(`/signing/envelopes/${envelope.id}/preview.pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
            catch (e) { toast.error("Could not open preview"); }
          }} data-testid="finalise-preview"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="finalise-confirm">Finalise & file</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
